/**
 * Redis implementation of DatabaseAdapter.
 *
 * Stores data in Redis with secondary indexes for efficient filtering.
 *
 * Data structure:
 * - {prefix}:{table}:{id} → JSON record
 * - {prefix}:{table}:_ids → Set with all IDs
 * - {prefix}:{table}:_idx:{field}:{value} → Set with IDs having that field value (secondary index)
 *
 * Performance characteristics:
 * - findById: O(1) - direct key lookup
 * - findMany with $eq filters: O(k) where k = matching records (uses index intersection)
 * - findMany with complex filters: O(n) where n = total records (loads all, filters in memory)
 */

import { Redis } from 'ioredis'
import type {
  DatabaseAdapter,
  PaginatedResult,
  ServerEntityQuery,
  FilterOperators
} from '@gzl10/nexus-sdk'
import { applyInMemoryFilters } from './filter-helpers.js'
import { NotFoundError } from '../../core/errors/app-error.js'

const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 20
const DEFAULT_MAX_LIMIT = 100

/** Fields that should not be indexed (internal/large) */
const NON_INDEXABLE_FIELDS = new Set(['id', 'createdAt', 'updatedAt', 'expires_at'])

/** Maximum value length for indexing (avoid huge index keys) */
const MAX_INDEX_VALUE_LENGTH = 256

function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

export interface RedisAdapterOptions {
  /** Redis connection URL (e.g., redis://localhost:6379) */
  url?: string
  /** Key prefix for all Redis keys (default: 'nexus') */
  prefix?: string
  /** Existing Redis client instance (takes precedence over url) */
  client?: Redis
  /** Enable secondary indexes for faster filtering (default: true) */
  enableIndexes?: boolean
}

interface StoredRecord {
  id: string
  createdAt: string
  updatedAt: string
  [key: string]: unknown
}

/** Result of analyzing filters for index usage */
interface FilterAnalysis {
  /** Filters that can use indexes (field → values) */
  indexable: Map<string, unknown[]>
  /** Remaining filters that need in-memory processing */
  remaining: Record<string, unknown>
  /** Whether all filters can be handled by indexes */
  fullyIndexable: boolean
}

/**
 * Redis database adapter with secondary indexes.
 *
 * @example
 * const adapter = createRedisAdapter({ url: 'redis://localhost:6379' })
 *
 * // Insert (automatically creates indexes)
 * const user = await adapter.insert('users', { name: 'John', status: 'active' })
 *
 * // Fast query using index
 * const result = await adapter.findMany<User>('users', {
 *   filters: { status: 'active' }  // Uses index: O(k) where k = active users
 * })
 *
 * // Complex query (falls back to scan)
 * const result = await adapter.findMany<User>('users', {
 *   filters: { name: { $contains: 'john' } }  // Scans all: O(n)
 * })
 */
export class RedisAdapter implements DatabaseAdapter {
  private client: Redis
  private prefix: string
  private ownsClient: boolean
  private enableIndexes: boolean

  constructor(options: RedisAdapterOptions = {}) {
    this.prefix = options.prefix ?? 'nexus'
    this.enableIndexes = options.enableIndexes ?? true

    if (options.client) {
      this.client = options.client
      this.ownsClient = false
    } else {
      this.client = new Redis(options.url ?? 'redis://localhost:6379', {
        lazyConnect: true,
        maxRetriesPerRequest: 3
      })
      this.ownsClient = true
    }
  }

  get raw(): Redis {
    return this.client
  }

  async connect(): Promise<void> {
    if (this.client.status === 'ready') return
    if (this.client.status === 'connecting') {
      await new Promise<void>((resolve, reject) => {
        this.client.once('ready', resolve)
        this.client.once('error', reject)
      })
      return
    }
    await this.client.connect()
  }

  async disconnect(): Promise<void> {
    if (this.ownsClient && this.client.status !== 'end') {
      await this.client.quit()
    }
  }

  async clear(): Promise<void> {
    const pattern = `${this.prefix}:*`
    const keys = await this.client.keys(pattern)
    if (keys.length > 0) {
      await this.client.del(...keys)
    }
  }

  async clearTable(table: string): Promise<void> {
    const pattern = `${this.prefix}:${table}:*`
    const keys = await this.client.keys(pattern)
    if (keys.length > 0) {
      await this.client.del(...keys)
    }
  }

  async seed<T extends Record<string, unknown>>(table: string, records: T[]): Promise<void> {
    for (const record of records) {
      await this.insert(table, record)
    }
  }

  // ===========================================================================
  // Key helpers
  // ===========================================================================

  private getRecordKey(table: string, id: string): string {
    return `${this.prefix}:${table}:${id}`
  }

  private getIdsKey(table: string): string {
    return `${this.prefix}:${table}:_ids`
  }

  private getIndexKey(table: string, field: string, value: unknown): string {
    const normalizedValue = this.normalizeIndexValue(value)
    return `${this.prefix}:${table}:_idx:${field}:${normalizedValue}`
  }

  private normalizeIndexValue(value: unknown): string {
    if (value === null) return '__null__'
    if (value === undefined) return '__undefined__'
    if (typeof value === 'boolean') return value ? '__true__' : '__false__'
    const str = String(value)
    return str.length > MAX_INDEX_VALUE_LENGTH ? str.substring(0, MAX_INDEX_VALUE_LENGTH) : str
  }

  private isIndexableValue(value: unknown): boolean {
    if (value === null || value === undefined) return true
    if (typeof value === 'string') return value.length <= MAX_INDEX_VALUE_LENGTH
    if (typeof value === 'number' || typeof value === 'boolean') return true
    return false
  }

  private parseRecord<T>(data: string | null): T | null {
    if (!data) return null
    try {
      return JSON.parse(data) as T
    } catch {
      return null
    }
  }

  // ===========================================================================
  // Index management
  // ===========================================================================

  /**
   * Add record to all applicable secondary indexes
   */
  private async addToIndexes(table: string, id: string, record: Record<string, unknown>, pipeline?: ReturnType<Redis['pipeline']>): Promise<void> {
    if (!this.enableIndexes) return

    const pipe = pipeline ?? this.client.pipeline()
    const shouldExec = !pipeline

    for (const [field, value] of Object.entries(record)) {
      if (NON_INDEXABLE_FIELDS.has(field)) continue
      if (!this.isIndexableValue(value)) continue

      const indexKey = this.getIndexKey(table, field, value)
      pipe.sadd(indexKey, id)
    }

    if (shouldExec) {
      await pipe.exec()
    }
  }

  /**
   * Remove record from all its secondary indexes
   */
  private async removeFromIndexes(table: string, id: string, record: Record<string, unknown>, pipeline?: ReturnType<Redis['pipeline']>): Promise<void> {
    if (!this.enableIndexes) return

    const pipe = pipeline ?? this.client.pipeline()
    const shouldExec = !pipeline

    for (const [field, value] of Object.entries(record)) {
      if (NON_INDEXABLE_FIELDS.has(field)) continue
      if (!this.isIndexableValue(value)) continue

      const indexKey = this.getIndexKey(table, field, value)
      pipe.srem(indexKey, id)
    }

    if (shouldExec) {
      await pipe.exec()
    }
  }

  /**
   * Update indexes when record fields change
   */
  private async updateIndexes(
    table: string,
    id: string,
    oldRecord: Record<string, unknown>,
    newRecord: Record<string, unknown>,
    pipeline?: ReturnType<Redis['pipeline']>
  ): Promise<void> {
    if (!this.enableIndexes) return

    const pipe = pipeline ?? this.client.pipeline()
    const shouldExec = !pipeline

    for (const [field, newValue] of Object.entries(newRecord)) {
      if (NON_INDEXABLE_FIELDS.has(field)) continue

      const oldValue = oldRecord[field]
      if (oldValue === newValue) continue

      // Remove from old index
      if (this.isIndexableValue(oldValue)) {
        const oldIndexKey = this.getIndexKey(table, field, oldValue)
        pipe.srem(oldIndexKey, id)
      }

      // Add to new index
      if (this.isIndexableValue(newValue)) {
        const newIndexKey = this.getIndexKey(table, field, newValue)
        pipe.sadd(newIndexKey, id)
      }
    }

    if (shouldExec) {
      await pipe.exec()
    }
  }

  // ===========================================================================
  // Filter analysis
  // ===========================================================================

  /**
   * Analyze filters to determine which can use indexes
   */
  private analyzeFilters(filters: Record<string, unknown>): FilterAnalysis {
    const indexable = new Map<string, unknown[]>()
    const remaining: Record<string, unknown> = {}
    let fullyIndexable = true

    for (const [field, condition] of Object.entries(filters)) {
      if (NON_INDEXABLE_FIELDS.has(field)) {
        remaining[field] = condition
        fullyIndexable = false
        continue
      }

      // Direct value: { status: 'active' } → $eq
      if (this.isIndexableValue(condition)) {
        indexable.set(field, [condition])
        continue
      }

      // Array shorthand: { status: ['active', 'pending'] } → $in
      if (Array.isArray(condition)) {
        const indexableValues = condition.filter(v => this.isIndexableValue(v))
        if (indexableValues.length === condition.length) {
          indexable.set(field, indexableValues)
          continue
        }
      }

      // Operator object: { status: { $eq: 'active' } }
      if (typeof condition === 'object' && condition !== null) {
        const ops = condition as FilterOperators

        // $eq is indexable
        if ('$eq' in ops && this.isIndexableValue(ops.$eq)) {
          indexable.set(field, [ops.$eq])
          // Check if there are other operators
          const otherOps = Object.keys(ops).filter(k => k !== '$eq')
          if (otherOps.length > 0) {
            remaining[field] = condition
            fullyIndexable = false
          }
          continue
        }

        // $in is indexable
        if ('$in' in ops && Array.isArray(ops.$in)) {
          const indexableValues = ops.$in.filter(v => this.isIndexableValue(v))
          if (indexableValues.length === ops.$in.length) {
            indexable.set(field, indexableValues)
            // Check if there are other operators
            const otherOps = Object.keys(ops).filter(k => k !== '$in')
            if (otherOps.length > 0) {
              remaining[field] = condition
              fullyIndexable = false
            }
            continue
          }
        }
      }

      // Not indexable
      remaining[field] = condition
      fullyIndexable = false
    }

    return { indexable, remaining, fullyIndexable }
  }

  /**
   * Get candidate IDs using secondary indexes.
   * Uses Redis SINTER when all fields have single-value equality filters.
   * Falls back to JS intersection when $in (multi-value) filters are involved.
   */
  private async getCandidateIds(table: string, indexable: Map<string, unknown[]>): Promise<string[] | null> {
    if (indexable.size === 0) return null

    // Fast path: all fields have single value → use native SINTER
    const allSingleValue = [...indexable.values()].every(v => v.length === 1)
    if (allSingleValue && indexable.size >= 2) {
      const keys = [...indexable].map(([field, values]) => this.getIndexKey(table, field, values[0]))
      return this.client.sinter(...keys)
    }

    // Collect ID sets for each filter field
    const fieldSets: Set<string>[] = []

    for (const [field, values] of indexable) {
      if (values.length === 1) {
        // Single value: direct lookup
        const indexKey = this.getIndexKey(table, field, values[0])
        const ids = await this.client.smembers(indexKey)
        fieldSets.push(new Set(ids))
      } else {
        // Multiple values ($in): union of all value indexes
        const unionSet = new Set<string>()
        for (const value of values) {
          const indexKey = this.getIndexKey(table, field, value)
          const ids = await this.client.smembers(indexKey)
          for (const id of ids) unionSet.add(id)
        }
        fieldSets.push(unionSet)
      }
    }

    if (fieldSets.length === 0) return null
    if (fieldSets.length === 1) return Array.from(fieldSets[0]!)

    // Intersect all field sets (AND logic between different fields)
    let result = fieldSets[0]!
    for (let i = 1; i < fieldSets.length; i++) {
      const nextSet = fieldSets[i]!
      result = new Set([...result].filter(id => nextSet.has(id)))
    }

    return Array.from(result)
  }

  // ===========================================================================
  // CRUD operations
  // ===========================================================================

  async findMany<T = unknown>(table: string, query?: ServerEntityQuery): Promise<PaginatedResult<T>> {
    const maxLimit = query?.maxLimit ?? DEFAULT_MAX_LIMIT
    const page = Math.max(1, query?.page ?? DEFAULT_PAGE)
    const limit = Math.min(maxLimit, Math.max(1, query?.limit ?? DEFAULT_LIMIT))
    const offset = (page - 1) * limit

    let candidateIds: string[] | null = null
    let remainingFilters: Record<string, unknown> | undefined

    // Try to use indexes for filtering
    if (query?.filters && this.enableIndexes) {
      const analysis = this.analyzeFilters(query.filters)

      if (analysis.indexable.size > 0) {
        candidateIds = await this.getCandidateIds(table, analysis.indexable)

        // No matches from index → empty result
        if (candidateIds && candidateIds.length === 0) {
          return { items: [], total: 0, page, limit, totalPages: 0, hasNext: false }
        }

        // If there are remaining filters, we need to apply them in memory
        if (Object.keys(analysis.remaining).length > 0) {
          remainingFilters = analysis.remaining
        }
      } else {
        // No indexable filters, will need full scan
        remainingFilters = query.filters
      }
    } else if (query?.filters) {
      remainingFilters = query.filters
    }

    // Get IDs to fetch
    let idsToFetch: string[]
    if (candidateIds !== null) {
      idsToFetch = candidateIds
    } else {
      const idsKey = this.getIdsKey(table)
      idsToFetch = await this.client.smembers(idsKey)
    }

    if (idsToFetch.length === 0) {
      return { items: [], total: 0, page, limit, totalPages: 0, hasNext: false }
    }

    // Fetch records
    const pipeline = this.client.pipeline()
    for (const id of idsToFetch) {
      pipeline.get(this.getRecordKey(table, id))
    }
    const results = await pipeline.exec()

    // Parse records
    let items: T[] = []
    if (results) {
      for (const result of results) {
        if (result) {
          const [err, data] = result
          if (!err && data) {
            const record = this.parseRecord<T>(data as string)
            if (record) items.push(record)
          }
        }
      }
    }

    // Apply remaining filters in memory (if any)
    if (remainingFilters && Object.keys(remainingFilters).length > 0) {
      items = applyInMemoryFilters(items, remainingFilters)
    }

    const total = items.length

    // Apply sorting
    if (query?.sort) {
      const sortField = query.sort
      const order = query.order ?? 'asc'
      items.sort((a, b) => {
        const aVal = (a as Record<string, unknown>)[sortField]
        const bVal = (b as Record<string, unknown>)[sortField]
        if (aVal === bVal) return 0
        if (aVal === null || aVal === undefined) return 1
        if (bVal === null || bVal === undefined) return -1
        const comparison = aVal < bVal ? -1 : 1
        return order === 'asc' ? comparison : -comparison
      })
    }

    // Apply pagination
    items = items.slice(offset, offset + limit)

    const totalPages = Math.ceil(total / limit)

    return {
      items,
      total,
      page,
      limit,
      totalPages,
      hasNext: page < totalPages
    }
  }

  async findOne<T = unknown>(table: string, filters: Record<string, unknown>): Promise<T | null> {
    const result = await this.findMany<T>(table, { filters, limit: 1 })
    return result.items[0] ?? null
  }

  async findById<T = unknown>(table: string, id: string): Promise<T | null> {
    const key = this.getRecordKey(table, id)
    const data = await this.client.get(key)
    return this.parseRecord<T>(data)
  }

  async count(table: string, filters?: Record<string, unknown>): Promise<number> {
    if (!filters) {
      const idsKey = this.getIdsKey(table)
      return await this.client.scard(idsKey)
    }

    // With filters, use the optimized findMany
    const result = await this.findMany(table, { filters, limit: 1 })
    return result.total
  }

  async insert<T = unknown>(table: string, data: Record<string, unknown>): Promise<T> {
    const id = (data['id'] as string) || generateId()
    const now = new Date().toISOString()

    const record: StoredRecord = {
      ...data,
      id,
      createdAt: now,
      updatedAt: now
    }

    const key = this.getRecordKey(table, id)
    const idsKey = this.getIdsKey(table)

    const pipeline = this.client.pipeline()
    pipeline.set(key, JSON.stringify(record))
    pipeline.sadd(idsKey, id)

    // Add to secondary indexes
    await this.addToIndexes(table, id, record, pipeline)

    await pipeline.exec()

    return record as T
  }

  async update<T = unknown>(table: string, id: string, data: Record<string, unknown>): Promise<T> {
    const key = this.getRecordKey(table, id)
    const existingData = await this.client.get(key)

    if (!existingData) {
      throw new NotFoundError(`Record "${id}" in table "${table}"`)
    }

    const existing = JSON.parse(existingData) as StoredRecord

    const updated: StoredRecord = {
      ...existing,
      ...data,
      id,
      createdAt: existing.createdAt,
      updatedAt: new Date().toISOString()
    }

    const pipeline = this.client.pipeline()
    pipeline.set(key, JSON.stringify(updated))

    // Update secondary indexes
    await this.updateIndexes(table, id, existing, updated, pipeline)

    await pipeline.exec()

    return updated as T
  }

  async delete(table: string, id: string): Promise<boolean> {
    const key = this.getRecordKey(table, id)
    const idsKey = this.getIdsKey(table)

    // Get record for index cleanup
    const existingData = await this.client.get(key)
    if (!existingData) return false

    const existing = JSON.parse(existingData) as StoredRecord

    const pipeline = this.client.pipeline()
    pipeline.del(key)
    pipeline.srem(idsKey, id)

    // Remove from secondary indexes
    await this.removeFromIndexes(table, id, existing, pipeline)

    const results = await pipeline.exec()
    return results !== null && results.length > 0
  }

  async transaction<T>(fn: (trx: DatabaseAdapter) => Promise<T>): Promise<T> {
    // Redis doesn't support SQL-style transactions with rollback
    return fn(this)
  }

  // ===========================================================================
  // TTL Methods
  // ===========================================================================

  async insertWithTtl<T = unknown>(table: string, data: Record<string, unknown>, ttlSeconds: number): Promise<T> {
    const id = (data['id'] as string) || generateId()
    const now = new Date().toISOString()
    const expires_at = new Date(Date.now() + ttlSeconds * 1000).toISOString()

    const record: StoredRecord = {
      ...data,
      id,
      createdAt: now,
      updatedAt: now,
      expires_at
    }

    const key = this.getRecordKey(table, id)
    const idsKey = this.getIdsKey(table)

    const pipeline = this.client.pipeline()
    pipeline.setex(key, ttlSeconds, JSON.stringify(record))
    pipeline.sadd(idsKey, id)

    // Add to secondary indexes (they'll need cleanup when record expires)
    await this.addToIndexes(table, id, record, pipeline)

    await pipeline.exec()

    return record as T
  }

  async setTtl(table: string, id: string, ttlSeconds: number): Promise<boolean> {
    const key = this.getRecordKey(table, id)

    const existingData = await this.client.get(key)
    if (!existingData) return false

    const existing = JSON.parse(existingData) as StoredRecord
    const expires_at = new Date(Date.now() + ttlSeconds * 1000).toISOString()

    const updated: StoredRecord = {
      ...existing,
      expires_at,
      updatedAt: new Date().toISOString()
    }

    await this.client.setex(key, ttlSeconds, JSON.stringify(updated))

    return true
  }

  async getTtl(table: string, id: string): Promise<number | null> {
    const key = this.getRecordKey(table, id)
    const ttl = await this.client.ttl(key)
    return ttl
  }

  async cleanupExpiredIds(table: string): Promise<number> {
    const idsKey = this.getIdsKey(table)
    const allIds = await this.client.smembers(idsKey)

    if (allIds.length === 0) return 0

    // Check which IDs still exist
    const pipeline = this.client.pipeline()
    for (const id of allIds) {
      pipeline.exists(this.getRecordKey(table, id))
    }
    const results = await pipeline.exec()

    // Collect expired IDs
    const expiredIds: string[] = []
    if (results) {
      for (let i = 0; i < results.length; i++) {
        const [err, exists] = results[i]!
        if (!err && exists === 0) {
          expiredIds.push(allIds[i]!)
        }
      }
    }

    // Remove expired IDs from the ID set
    // Note: Index cleanup for expired records happens lazily
    if (expiredIds.length > 0) {
      await this.client.srem(idsKey, ...expiredIds)
    }

    return expiredIds.length
  }

  /**
   * Rebuild all indexes for a table.
   * Useful after bulk imports or when indexes get out of sync.
   */
  async rebuildIndexes(table: string): Promise<number> {
    // Delete existing indexes
    const indexPattern = `${this.prefix}:${table}:_idx:*`
    const indexKeys = await this.client.keys(indexPattern)
    if (indexKeys.length > 0) {
      await this.client.del(...indexKeys)
    }

    // Rebuild from all records
    const idsKey = this.getIdsKey(table)
    const allIds = await this.client.smembers(idsKey)

    if (allIds.length === 0) return 0

    const pipeline = this.client.pipeline()
    for (const id of allIds) {
      pipeline.get(this.getRecordKey(table, id))
    }
    const results = await pipeline.exec()

    let indexedCount = 0
    if (results) {
      for (const result of results) {
        if (result) {
          const [err, data] = result
          if (!err && data) {
            const record = this.parseRecord<Record<string, unknown>>(data as string)
            if (record) {
              await this.addToIndexes(table, record['id'] as string, record)
              indexedCount++
            }
          }
        }
      }
    }

    return indexedCount
  }
}

export function createRedisAdapter(options?: RedisAdapterOptions): RedisAdapter {
  return new RedisAdapter(options)
}
